import { Component, input, output, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";

@TypeManagerDecorator("workflow-stages")
@Component({
    selector: "workflow-stages",
    template: `
        <div class="stages-container">
            <div class="stages-header">
                <h3>Stages</h3>
                <button class="btn btn-primary" (click)="addStage()">Add Stage</button>
            </div>

            <div class="stages-list">
                @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                    <div class="stage-item">
                        <div class="stage-main">
                            <div class="stage-info">
                                <s25-ng-editable-text
                                    [val]="stage.stageName"
                                    [hasCommit]="true"
                                    [hasCommitButton]="true"
                                    [hasCancelButton]="true"
                                    [customValidation]="getStageNameValidation(stage)"
                                    [onCommit]="getStageNameCommitHandler(stage)"
                                    placeholder="Stage name"
                                    class="stage-name"
                                ></s25-ng-editable-text>
                                <div class="stage-flags">
                                    <label class="flag">
                                        <input
                                            type="checkbox"
                                            [checked]="stage.isStartStage"
                                            (change)="updateStageStartStage(stage, $event)"
                                        />
                                        Start
                                    </label>
                                    <label class="flag">
                                        <input
                                            type="checkbox"
                                            [checked]="stage.isTerminal"
                                            (change)="updateStageTerminal(stage, $event)"
                                        />
                                        Terminal
                                    </label>
                                    <label class="flag">
                                        <input
                                            type="checkbox"
                                            [checked]="stage.defaultEnabled !== false"
                                            (change)="updateStageDefaultEnabled(stage, $event)"
                                        />
                                        Enabled
                                    </label>
                                </div>
                            </div>
                            <div class="stage-actions">
                                <button class="btn btn-sm" (click)="toggleStageCollapse(stage)">
                                    {{ isStageCollapsed(stage) ? "▼" : "▲" }}
                                </button>
                                <button class="btn btn-sm btn-danger" (click)="deleteStage(stage, $event)">×</button>
                            </div>
                        </div>

                        @if (!isStageCollapsed(stage)) {
                            <div class="stage-details">
                                <div class="stage-description">
                                    <textarea
                                        [(ngModel)]="stage.description"
                                        (ngModelChange)="onStageChange()"
                                        placeholder="Description (optional)"
                                        rows="2"
                                    ></textarea>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    `,
    styles: [
        `
            .stages-container {
                padding: 20px;
            }

            .stages-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 20px;
            }

            .stages-header h3 {
                margin: 0;
                color: #333;
            }

            .btn {
                padding: 8px 16px;
                border: 1px solid #ddd;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s;
            }

            .btn-primary {
                background: #007bff;
                color: white;
                border-color: #007bff;
            }

            .btn-primary:hover {
                background: #0056b3;
            }

            .btn-sm {
                padding: 4px 8px;
                font-size: 12px;
            }

            .btn-danger {
                background: #dc3545;
                color: white;
                border-color: #dc3545;
            }

            .btn-danger:hover {
                background: #c82333;
            }

            .stages-list {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .stage-item {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                overflow: hidden;
            }

            .stage-main {
                padding: 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: #f8f9fa;
            }

            .stage-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .stage-name {
                font-weight: 500;
                color: #333;
            }

            .stage-flags {
                display: flex;
                gap: 16px;
            }

            .flag {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                color: #666;
                cursor: pointer;
            }

            .flag input {
                margin: 0;
            }

            .stage-actions {
                display: flex;
                gap: 8px;
            }

            .stage-details {
                padding: 20px;
                border-top: 1px solid #e0e0e0;
            }

            .stage-description {
                margin-bottom: 20px;
            }

            .stage-description textarea {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 14px;
                resize: vertical;
            }

            .stage-sections {
                display: flex;
                flex-direction: column;
                gap: 20px;
            }

            .section h4 {
                margin: 0 0 12px 0;
                color: #333;
                font-size: 14px;
                font-weight: 500;
            }
        `,
    ],
    standalone: true,
    imports: [CommonModule, FormsModule, S25EditableTextComponent],
})
export class WorkflowStagesComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    workflowProcessChange = output<WorkflowTypes.WorkflowProcess>();

    private collapsedStages = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.initializeStages();
    }

    private initializeStages() {
        if (!this.workflowProcess().stages) {
            this.workflowProcess().stages = [];
        }

        // Initialize stage arrays if needed
        for (const stage of this.workflowProcess().stages) {
            if (!stage.actions) {
                stage.actions = [];
            }
            if (!stage.states) {
                stage.states = [];
            }
        }
    }

    addStage() {
        const newStage: WorkflowTypes.WorkflowStage = {
            id: this.getNextNegativeId(),
            workflowProcessId: this.workflowProcess().workflowProcessId,
            stageName: `Stage ${this.workflowProcess().stages!.length + 1}`,
            isTerminal: false,
            isStartStage: false,
            description: "",
            defaultEnabled: true,
            states: [],
            actions: [],
        };

        this.workflowProcess().stages!.push(newStage);
        this.onStageChange();
    }

    private getNextNegativeId(): number {
        const stages = this.workflowProcess().stages || [];
        return stages.reduce((minId, stage) => Math.min(minId, stage.id || -1), -1) - 1;
    }

    deleteStage(stage: WorkflowTypes.WorkflowStage, event: Event) {
        event.stopPropagation();

        const index = this.workflowProcess().stages!.findIndex(
            (s) => (s.workflowStageId && s.workflowStageId === stage.workflowStageId) || (s.id && s.id === stage.id),
        );

        if (index > -1) {
            this.workflowProcess().stages!.splice(index, 1);
            this.onStageChange();
        }
    }

    toggleStageCollapse(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const collapsed = this.collapsedStages();

        if (collapsed.has(stageKey)) {
            collapsed.delete(stageKey);
        } else {
            collapsed.add(stageKey);
        }

        this.collapsedStages.set(new Set(collapsed));
    }

    isStageCollapsed(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.collapsedStages().has(stageKey);
    }

    updateStageTerminal(stage: WorkflowTypes.WorkflowStage, event: Event) {
        const target = event.target as HTMLInputElement;
        stage.isTerminal = target.checked;
        this.onStageChange();
    }

    updateStageDefaultEnabled(stage: WorkflowTypes.WorkflowStage, event: Event) {
        const target = event.target as HTMLInputElement;
        stage.defaultEnabled = target.checked;
        this.onStageChange();
    }

    updateStageStartStage(stage: WorkflowTypes.WorkflowStage, event: Event) {
        const target = event.target as HTMLInputElement;
        const isStartStage = target.checked;

        // If setting this stage as start stage, unset all other stages
        if (isStartStage) {
            this.workflowProcess().stages!.forEach((s) => {
                if (s !== stage) {
                    s.isStartStage = false;
                }
            });
        }

        stage.isStartStage = isStartStage;
        this.onStageChange();
    }

    onStageChange() {
        this.workflowProcessChange.emit(this.workflowProcess());
        this.cd.detectChanges();
    }

    getStageNameValidation(stage: WorkflowTypes.WorkflowStage) {
        return (value: string) => {
            if (!value || value.trim().length === 0) {
                return ["Stage name is required"];
            }

            // Check for duplicate stage names
            const duplicateStage = this.workflowProcess().stages!.find(
                (s) =>
                    s.stageName === value.trim() &&
                    ((s.workflowStageId && s.workflowStageId !== stage.workflowStageId) || (s.id && s.id !== stage.id)),
            );

            if (duplicateStage) {
                return ["Stage name must be unique within the workflow"];
            }

            return true;
        };
    }

    getStageNameCommitHandler(stage: WorkflowTypes.WorkflowStage) {
        return (value: string) => {
            stage.stageName = value;
            this.onStageChange();
        };
    }
}
